


























































































































































import {
	computed,
	defineComponent,
	PropType,
	Ref,
	ref,
} from '@vue/composition-api';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { CommunityWithGroups } from '@front-app/services/CommunityApi/CommunityModel';
import { Size, Spacing } from '@front-app/components/constants';
import GroupCard from '@front-app/components/cards/GroupCard.vue';
import { GroupsStore } from '@front-app/store/singletons/Groups';
import { SearchOption } from '@front-app/components/search/useSearch';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import { EventsStore } from '@front-app/store/singletons/Events';
import EventCard from '@front-app/components/cards/EventCard.vue';
import { GroupEventType } from '@front-app/services/GroupsApi/GroupEvent';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import SearchBox from '@front-app/components/search/SearchBox.vue';
import SearchResultComponent from '@front-app/components/search/SearchResult.vue';
import SearchPlaceholder from '@front-app/components/search/SearchPlaceholder.vue';
import SuccessCard from '@front-app/components/cards/state/SuccessCard.vue';
import { useCommunitiesStore } from '@front-app/store/piniaModules/communities';
import { useContentSearch } from '@front-app/hooks/useContentSearch';
import { useRollingIndex } from '@front-app/components/search/useRollingIndex';
import { SearchResult, SearchResultType } from '@front-app/services/SearchApi/SearchApi';
import { SearchType } from '@front-app/services/SearchApi/SearchModel';
import SimpleDropdown from '@front-app/components/dropdowns/SimpleDropdown.vue';
import ButtonList from '@front-app/components/dropdowns/ButtonList.vue';
import PublicLink from '@front-app/components/buttons/PublicLink.vue';
import LinkEventsModal from './LinkEventsModal.vue';

export default defineComponent({
	components: {
		GroupCard,
		CtaButton,
		EventCard,
		ErrorCard,
		SearchBox,
		SearchResultComponent,
		SearchPlaceholder,
		SuccessCard,
		LoaderState,
		SimpleDropdown,
		ButtonList,
		PublicLink,
		LinkEventsModal,
	},
	props: {
		community: {
			type: Object as PropType<CommunityWithGroups>,
			required: false,
			default: null,
		},
		isOpen: Boolean,
	},
	setup(props) {
		// Search
		const isSearchLoading = ref(true);
		const searchValue = ref('');
		const allSearchResults = ref([] as SearchOption<SearchResult>[]);
		async function searchContent(searchString: string) {
			isSearchLoading.value = true;
			allSearchResults.value = await useContentSearch(SearchType.FULL, SearchResultType.GROUP, searchString);
			isSearchLoading.value = false;
		}
		const searchFocus = useRollingIndex(allSearchResults);
		const focusedSearchIdx = searchFocus.selectedIdx;
		const focusNextSearchResult = searchFocus.next;
		const focusPrevSearchResult = searchFocus.prev;
		const clearSearch = () => {
			focusedSearchIdx.value = null;
			searchValue.value = '';
		};

		const communitiesStore = useCommunitiesStore();

		const {
			track: trackGroupEventsLoad,
			isError: isGroupEventsError,
			isLoading: isGroupEventsLoading,
		} = useAsyncStatus();
		trackGroupEventsLoad(Promise.all([
			GroupsStore.getInstance().fetchGroupsOnce(),
			EventsStore.getInstance().getEventsOnce(),
		]));

		const {
			track,
			isError,
			isLoading,
			isSuccess,
		} = useAsyncStatus();
		const is409Error = ref(false);
		const handleGroupConflictError = () => {
			is409Error.value = true;
		};
		const addGroup = async (request: SearchOption<SearchResult>) => {
			// eslint-disable-next-line no-alert
			const shouldContinue = window.confirm('Are you sure you want to add this group or event to the community?');
			if (!shouldContinue) {
				return;
			}
			clearSearch();
			is409Error.value = false;
			track(communitiesStore.addCommunityGroups(props.community.id, [request.original.id], handleGroupConflictError));
		};
		const addFirstSearchResult = () => {
			if (allSearchResults.value.length === 0) {
				return;
			}
			const firstResult = allSearchResults.value[0];
			addGroup(firstResult);
		};

		const checkmarkedGroupIds: Ref<string[]> = ref([]);
		const toggleGroupChecked = (id: string) => {
			if (checkmarkedGroupIds.value.includes(id)) {
				checkmarkedGroupIds.value = checkmarkedGroupIds.value.filter((oldId) => oldId !== id);
			} else {
				checkmarkedGroupIds.value = [id, ...checkmarkedGroupIds.value];
			}
		};

		// Minimal Actions + Modal state
		const isActionsOpen = ref(false);
		const isLinkEventsOpen = ref(false);
		const selectedGroupIds = computed(() => checkmarkedGroupIds.value);
		const openLinkEventsModal = () => {
			isActionsOpen.value = false;
			isLinkEventsOpen.value = true;
		};
		const selectedGroupsLabel = computed(() => {
			const n = selectedGroupIds.value.length;
			return `${n} group${n === 1 ? '' : 's'} selected`;
		});
		const selectedGroupNames = computed(() => {
			const all = props.community?.groups || [];
			return selectedGroupIds.value.map((id: string) => {
				const rec = all.find((g: any) => g.groupId === id);
				return (rec && rec.group && rec.group.name) ? rec.group.name : id;
			});
		});

		const {
			track: trackGroupRemoval,
			isLoading: isRemovingGroups,
		} = useAsyncStatus();
		const removeGroups = async () => {
			// eslint-disable-next-line no-alert
			const shouldContinue = window.confirm('Are you sure you want to remove these items from the community?');
			if (!shouldContinue) {
				return;
			}
			await trackGroupRemoval(communitiesStore.removeCommunityGroups(props.community.id, checkmarkedGroupIds.value));
			checkmarkedGroupIds.value = [];
		};

		const communityGroups = computed(() => props.community.groups.filter(({ type }) => type === GroupEventType.GROUP));
		const communityEvents = computed(() => props.community.groups.filter(({ type }) => type === GroupEventType.EVENT));

		return {
			// Initial load
			isGroupEventsError,
			isGroupEventsLoading,
			// Search
			addFirstSearchResult,
			clearSearch,
			focusedSearchIdx,
			focusNextSearchResult,
			focusPrevSearchResult,
			searchValue,
			allSearchResults,
			searchContent,
			isSearchLoading,
			// Add group
			addGroup,
			is409Error,
			isError,
			isLoading,
			isSuccess,
			// Groups
			groupById: GroupsStore.getInstance().groupById,
			eventById: EventsStore.getInstance().eventById,
			checkmarkedGroupIds,
			toggleGroupChecked,
			isActionsOpen,
			isLinkEventsOpen,
			selectedGroupIds,
			selectedGroupsLabel,
			selectedGroupNames,
			openLinkEventsModal,
			isRemovingGroups,
			removeGroups,
			communityGroups,
			communityEvents,
			// Util
			Spacing,
			Size,
		};
	},
});
